import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

// Grid view for the Documents tab.
//
// Portfolio level: locations as rows, dates as columns. A cell is the count of
// documents received for that location on that day; any day where a document
// type the location normally sends is missing renders GREEN so gaps pop.
//
// Clicking a cell (or a location name) drills into the location: document
// types as rows, the same dates as columns, with the clicked day's column
// highlighted and each missing (type, day) cell green.
//
// "Missing" is inferred, not configured: a type counts as missing on a day
// only if that location sent that type on some other day in the window —
// documents have no delivery schedule the way ETL report feeds do.

function listDates(startDate, endDate) {
  const out = [];
  const cursor = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  while (cursor.getTime() <= end.getTime()) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

function docDate(doc) {
  const raw = doc.effectiveDate || doc.receivedAt;
  if (!raw) return null;
  return String(raw).slice(0, 10);
}

function typeKeyOf(doc) {
  if (doc.source === 'etl') return `etl:${doc.fileType || 'report'}`;
  return `doc:${doc.matchKey || doc.displayName || doc.originalFileName || 'document'}`;
}

function typeLabelOf(doc) {
  return doc.displayName || doc.originalFileName || doc.fileType || 'Document';
}

function shortDate(iso) {
  const [, m, d] = iso.split('-');
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
}

function isWeekend(iso) {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

export default function DocumentsGrid({ documents, locationNameById, startDate, endDate }) {
  const [drill, setDrill] = useState(null); // { locationId, date | null }
  const scrollRef = useRef(null);

  const dates = useMemo(() => listDates(startDate, endDate), [startDate, endDate]);

  // location → { types: Map(typeKey → label), byDateType: Set('date::typeKey'),
  //              countByDate: Map(date → count) }
  const byLocation = useMemo(() => {
    const map = new Map();
    for (const doc of documents) {
      const date = docDate(doc);
      if (!date) continue;
      const locId = doc.locationId;
      if (!map.has(locId)) {
        map.set(locId, { types: new Map(), byDateType: new Set(), countByDate: new Map() });
      }
      const bucket = map.get(locId);
      const tk = typeKeyOf(doc);
      if (!bucket.types.has(tk)) bucket.types.set(tk, typeLabelOf(doc));
      bucket.byDateType.add(`${date}::${tk}`);
      bucket.countByDate.set(date, (bucket.countByDate.get(date) || 0) + 1);
    }
    return map;
  }, [documents]);

  const locationIds = useMemo(
    () => [...byLocation.keys()].sort((a, b) => {
      const an = locationNameById.get(a) || `Hotel ${a}`;
      const bn = locationNameById.get(b) || `Hotel ${b}`;
      return an.localeCompare(bn);
    }),
    [byLocation, locationNameById],
  );

  // Start scrolled to the most recent dates.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [drill, locationIds.length]);

  if (documents.length === 0) {
    return (
      <div className="p-10 text-center text-[13px] text-gray-400">
        No documents in this period to chart.
      </div>
    );
  }

  // ----- Location drill-in: types × dates -----------------------------------
  if (drill) {
    const bucket = byLocation.get(drill.locationId);
    const locName = locationNameById.get(drill.locationId) || `Hotel ${drill.locationId}`;
    const typeEntries = bucket
      ? [...bucket.types.entries()].sort((a, b) => a[1].localeCompare(b[1]))
      : [];

    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-3 md:px-4 py-2 border-b border-gray-100 flex items-center gap-2">
          <button
            onClick={() => setDrill(null)}
            className="flex items-center gap-1 px-2 py-1 text-[12px] font-medium text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <ChevronLeft size={14} /> All locations
          </button>
          <span className="text-[13px] font-semibold text-gray-800">{locName}</span>
          {drill.date && (
            <span className="text-[11px] text-gray-500">
              — {shortDate(drill.date)} highlighted
            </span>
          )}
          <span className="ml-auto text-[11px] text-gray-400 hidden md:block">
            green = expected type not received that day
          </span>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-auto">
          <table className="text-[11px] border-separate border-spacing-0">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="sticky left-0 bg-white text-left px-3 py-1.5 font-medium text-gray-500 min-w-[180px] max-w-[240px] border-b border-gray-100">
                  Document type
                </th>
                {dates.map((date) => (
                  <th
                    key={date}
                    className={`px-0.5 py-1.5 font-normal border-b border-gray-100 text-center min-w-[30px] ${
                      date === drill.date
                        ? 'bg-orange-100 text-orange-800 font-semibold'
                        : isWeekend(date) ? 'text-gray-300' : 'text-gray-400'
                    }`}
                  >
                    {shortDate(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {typeEntries.map(([tk, label]) => (
                <tr key={tk}>
                  <td
                    className="sticky left-0 bg-white px-3 py-1 text-gray-700 font-medium truncate min-w-[180px] max-w-[240px] border-b border-gray-50"
                    title={label}
                  >
                    {label}
                  </td>
                  {dates.map((date) => {
                    const has = bucket.byDateType.has(`${date}::${tk}`);
                    const selected = date === drill.date;
                    let cellClass;
                    if (has) {
                      cellClass = selected ? 'bg-orange-200' : 'bg-gray-200';
                    } else {
                      // Missing — green, stronger inside the selected column so
                      // the missing type for the drilled day pops immediately.
                      cellClass = selected ? 'bg-green-500' : 'bg-green-100';
                    }
                    return (
                      <td key={date} className={`border-b border-gray-50 p-0.5 ${selected ? 'bg-orange-50' : ''}`}>
                        <div
                          className={`w-6 h-6 mx-auto rounded ${cellClass}`}
                          title={`${label} — ${shortDate(date)}: ${has ? 'received' : 'missing'}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ----- Portfolio: locations × dates ---------------------------------------
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-3 md:px-4 py-2 border-b border-gray-100 flex items-center gap-2">
        <span className="text-[12px] text-gray-500">
          Click a day to see which document types are missing at that location.
        </span>
        <span className="ml-auto text-[11px] text-gray-400 hidden md:block">
          green = a document type this location normally sends is missing
        </span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-auto">
        <table className="text-[11px] border-separate border-spacing-0">
          <thead className="sticky top-0 bg-white z-10">
            <tr>
              <th className="sticky left-0 bg-white text-left px-3 py-1.5 font-medium text-gray-500 min-w-[180px] max-w-[240px] border-b border-gray-100">
                Location
              </th>
              {dates.map((date) => (
                <th
                  key={date}
                  className={`px-0.5 py-1.5 font-normal border-b border-gray-100 text-center min-w-[30px] ${isWeekend(date) ? 'text-gray-300' : 'text-gray-400'}`}
                >
                  {shortDate(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {locationIds.map((locId) => {
              const bucket = byLocation.get(locId);
              const locName = locationNameById.get(locId) || `Hotel ${locId}`;
              const totalTypes = bucket.types.size;
              return (
                <tr key={locId}>
                  <td className="sticky left-0 bg-white px-3 py-1 border-b border-gray-50 min-w-[180px] max-w-[240px]">
                    <button
                      onClick={() => setDrill({ locationId: locId, date: null })}
                      className="text-gray-700 font-medium hover:text-orange-600 truncate text-left w-full"
                      title={`${locName} — ${totalTypes} document type${totalTypes === 1 ? '' : 's'}`}
                    >
                      {locName}
                    </button>
                  </td>
                  {dates.map((date) => {
                    let received = 0;
                    for (const tk of bucket.types.keys()) {
                      if (bucket.byDateType.has(`${date}::${tk}`)) received += 1;
                    }
                    const missing = totalTypes - received;
                    let cellClass;
                    if (received === 0) {
                      cellClass = 'bg-green-100 text-green-800'; // everything missing
                    } else if (missing > 0) {
                      cellClass = 'bg-green-100 text-green-800'; // partial — something missing
                    } else {
                      cellClass = 'bg-gray-200 text-gray-700'; // complete day
                    }
                    return (
                      <td key={date} className="border-b border-gray-50 p-0.5">
                        <button
                          onClick={() => setDrill({ locationId: locId, date })}
                          className={`w-6 h-6 mx-auto rounded flex items-center justify-center font-medium hover:ring-2 hover:ring-orange-400 ${cellClass}`}
                          title={`${locName} — ${shortDate(date)}: ${received}/${totalTypes} document types received${missing > 0 ? `, ${missing} missing` : ''}`}
                        >
                          {received > 0 ? received : ''}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
